import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, styled } from "@mui/material";
import { DateTime } from 'luxon';

import { AppDispatch } from '@/data/store';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import { fetchPassengerClientDataAsync, selectPassengerClientData } from '@/data/client.slice';
import { fetchPassengerAsync, selectCurrentPassenger, selectLoadedCurrentPassenger } from '@/data/passenger.slice';
import { fetchPassengerBenefitSummaryAsync } from '@/data/reservation.slice';
import * as lookupSlice from '@/data/lookup.slice';
import * as accountingSlice from '@/data/accounting.slice';

import { CreateReservationDialog } from './CreateReservationDialog';
import { ReservationPortalProvider } from './ReservationPortalContext';

const PREFIX = 'CreateReservationPage';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
  },
}));

const CreateReservationPage = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const passenger = useAppSelector(selectCurrentPassenger);
  const loadedCurrentPassenger = useAppSelector(selectLoadedCurrentPassenger);
  const passengerClientData = useAppSelector(selectPassengerClientData);

  const handleClose = () => {
    navigate(`/passengers/${params.id}`);
  };

  useEffect(() => {
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(accountingSlice.fetchTransportationAttributesAsync());

    if (params.id && passenger?.passengerUUID !== params.id) {
      dispatch(fetchPassengerAsync(params.id));
    }
    if (params.id && passenger?.passengerUUID && params.id === passenger.passengerUUID) {
      dispatch(
        fetchPassengerBenefitSummaryAsync(
          passenger.passengerUUID,
          passenger.servicePlans.map(({ servicePlanUUID }) => servicePlanUUID),
          DateTime.now().toISODate(),
        ),
      );
      dispatch(
        fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
        ),
      );
    }
  }, [params.id, passenger?.passengerUUID, dispatch]);

  useEffect(() => {
    if (loadedCurrentPassenger && passenger) {
      dispatch(
        fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
        ),
      );
    }
  }, [loadedCurrentPassenger, passenger]);

  return (
    <StyledBox className={classes.container}>
      <ReservationPortalProvider isPortal={false}>
        <CreateReservationDialog
          open={true}
          onClose={handleClose}
          passenger={passenger}
          servicePlans={passengerClientData?.servicePlans ?? []}
        />
      </ReservationPortalProvider>
    </StyledBox>
  );
};

export { CreateReservationPage };
